import React, { useState, useEffect, useCallback } from 'react';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Eye, 
  EyeOff, 
  Star,
  Save,
  X,
  AlertTriangle,
  CreditCard,
  ArrowLeft,
  Home,
  Building2,
  Store,
  RefreshCw,
} from 'lucide-react';
import websiteApi from '../../services/websiteApi';
import './PricingPlansManager.css';

const CATEGORIES = [
  { id: 'home' as const, label: 'Home / Residential', desc: 'Individual users and families.' },
  { id: 'enterprise' as const, label: 'Enterprise', desc: 'Businesses and organizations.' },
  { id: 'retail' as const, label: 'Retail', desc: 'Retail stores and small businesses.' },
];

interface PricingPlanData {
  _id?: string;
  name: string;
  price: {
    monthly: number;
    yearly: number;
  };
  period: string;
  description?: string;
  features: string[];
  isFree: boolean;
  isCustom: boolean;
  popular: boolean;
  isActive: boolean;
  sortOrder: number;
  category?: 'home' | 'enterprise' | 'retail';
  originalPrice?: {
    monthly: number;
    yearly: number;
  };
  createdAt?: string;
  updatedAt?: string;
}

const PricingPlansManager: React.FC = () => {
  const [plans, setPlans] = useState<PricingPlanData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [showModal, setShowModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState<PricingPlanData | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);
  
  // Category selection state
  const [selectedCategory, setSelectedCategory] = useState<'home' | 'enterprise' | 'retail' | null>(null);
  
  const [formData, setFormData] = useState<PricingPlanData>({
    name: '',
    price: { monthly: 0, yearly: 0 },
    period: 'per month',
    features: [''],
    isActive: true,
    popular: false,
    isFree: false,
    isCustom: false,
    sortOrder: 1,
    description: '',
    category: 'home'
  });

  const fetchPlans = useCallback(async () => {
    if (!selectedCategory) return;
    
    try {
      setLoading(true);
      const fetchedPlans = await websiteApi.getPricingPlans(selectedCategory);
      setPlans(fetchedPlans);
      setError('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch plans');
    } finally {
      setLoading(false);
    }
  }, [selectedCategory]);

  useEffect(() => {
    if (selectedCategory) {
      fetchPlans();
    }
  }, [selectedCategory, fetchPlans]);

  const handleSave = async () => {
    try {
      if (editingPlan?._id) {
        // Update existing plan - exclude _id field
        const { _id, ...updateData } = formData;
        await websiteApi.updatePricingPlan(editingPlan._id, updateData);
      } else {
        // Create new plan
        await websiteApi.createPricingPlan(formData);
      }

      await fetchPlans();
      setShowModal(false);
      setEditingPlan(null);
      resetForm();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save plan');
    }
  };

  const handleDelete = async (planId: string) => {
    try {
      await websiteApi.deletePricingPlan(planId);
      await fetchPlans();
      setShowDeleteConfirm(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete plan');
    }
  };

  const handleToggleActive = async (plan: PricingPlanData) => {
    try {
      await websiteApi.togglePricingPlanStatus(plan._id!, !plan.isActive);
      await fetchPlans();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to toggle plan status');
    }
  };

  const openEditModal = (plan?: PricingPlanData) => {
    if (plan) {
      setEditingPlan(plan);
      setFormData(plan);
    } else {
      setEditingPlan(null);
      resetForm();
    }
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      price: { monthly: 0, yearly: 0 },
      period: 'per month',
      features: [''],
      isActive: true,
      popular: false,
      isFree: false,
      isCustom: false,
      sortOrder: 1,
      description: '',
      category: selectedCategory || 'home'
    });
  };

  const addFeature = () => {
    setFormData(prev => ({
      ...prev,
      features: [...prev.features, '']
    }));
  };

  const removeFeature = (index: number) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index)
    }));
  };

  const updateFeature = (index: number, value: string) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.map((feature, i) => i === index ? value : feature)
    }));
  };

  const categoryLabel = CATEGORIES.find((c) => c.id === selectedCategory)?.label || '';

  if (!selectedCategory) {
    return (
      <div className="pp-page">
        <div className="pp-toolbar">
          <span className="pp-toolbar-note">Select a category to manage pricing plans</span>
        </div>
        <div className="pp-category-grid">
          {CATEGORIES.map((cat) => {
            const Icon = cat.id === 'home' ? Home : cat.id === 'enterprise' ? Building2 : Store;
            return (
              <button key={cat.id} type="button" className="pp-category-card" onClick={() => setSelectedCategory(cat.id)}>
                <div className="pp-category-icon"><Icon size={18} /></div>
                <h3 className="pp-category-title">{cat.label}</h3>
                <p className="pp-category-desc">{cat.desc}</p>
                <span className="pp-category-meta">Free · Basic · Pro · Enterprise</span>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="pp-page pp-loading">
        <RefreshCw size={20} className="pp-spin" />
        Loading plans…
      </div>
    );
  }

  return (
    <div className="pp-page">
      <div className="pp-toolbar">
        <button type="button" className="pp-btn" onClick={() => setSelectedCategory(null)}>
          <ArrowLeft size={14} /> Categories
        </button>
        <span className="pp-toolbar-note">{categoryLabel} · {plans.length} plan{plans.length === 1 ? '' : 's'}</span>
        <div className="pp-toolbar-actions">
          <button type="button" className="pp-btn pp-btn--primary" onClick={() => openEditModal()}>
            <Plus size={14} /> Add plan
          </button>
        </div>
      </div>

      {error && (
        <div className="pp-toast pp-toast--err">
          <span>{error}</span>
          <button type="button" onClick={() => setError('')} aria-label="Dismiss"><X size={14} /></button>
        </div>
      )}

      <div className="pp-table-panel">
        {plans.length === 0 ? (
          <div className="pp-empty">
            <CreditCard size={28} strokeWidth={1.5} />
            <p>No plans in this category yet</p>
            <button type="button" className="pp-btn pp-btn--primary" onClick={() => openEditModal()}>
              <Plus size={14} /> Create plan
            </button>
          </div>
        ) : (
          <div className="pp-table-scroll">
            <table className="pp-table">
              <thead>
                <tr>
                  <th>Plan</th>
                  <th>Price</th>
                  <th>Features</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {plans.map((plan) => (
                  <tr key={plan._id}>
                    <td>
                      <span className="pp-plan-name">
                        {plan.name}
                        {plan.popular && <Star size={12} style={{ marginLeft: 4, color: '#eab308', verticalAlign: 'middle' }} />}
                      </span>
                      <span className="pp-plan-desc">{plan.description || 'No description'}</span>
                    </td>
                    <td>
                      {plan.isCustom || (plan.price.monthly === 0 && plan.price.yearly === 0) ? (
                        <span>{plan.period || 'Custom'}</span>
                      ) : (
                        <>
                          <div>₹{plan.price.monthly.toLocaleString()}/mo · ₹{plan.price.yearly.toLocaleString()}/mo (yr)</div>
                          <span className="pp-plan-desc">{plan.period}</span>
                        </>
                      )}
                    </td>
                    <td>
                      <span>{plan.features.filter(Boolean).length} features</span>
                      <span className="pp-plan-desc">Order {plan.sortOrder}</span>
                    </td>
                    <td>
                      <span className={`pp-badge ${plan.isActive ? 'pp-badge--active' : 'pp-badge--inactive'}`}>
                        {plan.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <div className="pp-actions">
                        <button type="button" className="pp-icon-btn" title={plan.isActive ? 'Deactivate' : 'Activate'} onClick={() => handleToggleActive(plan)}>
                          {plan.isActive ? <EyeOff size={14} /> : <Eye size={14} />}
                        </button>
                        <button type="button" className="pp-icon-btn" title="Edit" onClick={() => openEditModal(plan)}>
                          <Edit size={14} />
                        </button>
                        <button type="button" className="pp-icon-btn pp-icon-btn--danger" title="Delete" onClick={() => setShowDeleteConfirm(plan._id!)}>
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <div className="pp-overlay" onClick={() => setShowModal(false)}>
          <div className="pp-modal" onClick={(e) => e.stopPropagation()}>
            <div className="pp-modal-header">
              <h3 className="pp-modal-title">{editingPlan ? 'Edit plan' : 'New plan'}</h3>
              <button type="button" className="pp-modal-close" onClick={() => setShowModal(false)} aria-label="Close">
                <X size={16} />
              </button>
            </div>
            <div className="pp-modal-body">
              <div className="pp-form-row">
                <div className="pp-field">
                  <label htmlFor="pp-name">Plan name</label>
                  <input id="pp-name" type="text" value={formData.name} onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))} placeholder="Pro plan" />
                </div>
                <div className="pp-field">
                  <label htmlFor="pp-sort">Sort order</label>
                  <input id="pp-sort" type="number" value={formData.sortOrder} onChange={(e) => setFormData((prev) => ({ ...prev, sortOrder: Number(e.target.value) }))} min={1} />
                </div>
              </div>
              {formData.isCustom ? (
                <div className="pp-field" style={{ marginTop: 12 }}>
                  <label htmlFor="pp-custom">Custom pricing text</label>
                  <input id="pp-custom" type="text" value={formData.period} onChange={(e) => setFormData((prev) => ({ ...prev, period: e.target.value }))} placeholder="Contact us for pricing" />
                </div>
              ) : (
                <div className="pp-form-row" style={{ marginTop: 12 }}>
                  <div className="pp-field">
                    <label htmlFor="pp-monthly">Monthly (₹)</label>
                    <input id="pp-monthly" type="number" value={formData.price.monthly} onChange={(e) => setFormData((prev) => ({ ...prev, price: { ...prev.price, monthly: Number(e.target.value) } }))} min={0} step={0.01} />
                  </div>
                  <div className="pp-field">
                    <label htmlFor="pp-yearly">Yearly equiv. (₹/mo)</label>
                    <input id="pp-yearly" type="number" value={formData.price.yearly} onChange={(e) => setFormData((prev) => ({ ...prev, price: { ...prev.price, yearly: Number(e.target.value) } }))} min={0} step={0.01} />
                  </div>
                </div>
              )}
              {!formData.isCustom && (
                <div className="pp-field" style={{ marginTop: 12 }}>
                  <label htmlFor="pp-period">Billing period label</label>
                  <input id="pp-period" type="text" value={formData.period} onChange={(e) => setFormData((prev) => ({ ...prev, period: e.target.value }))} placeholder="per month" />
                </div>
              )}
              <div className="pp-field" style={{ marginTop: 12 }}>
                <label htmlFor="pp-desc">Description</label>
                <input id="pp-desc" type="text" value={formData.description || ''} onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))} />
              </div>
              <div className="pp-field" style={{ marginTop: 12 }}>
                <label>Features</label>
                {formData.features.map((feature, index) => (
                  <div key={index} className="pp-feature-row">
                    <input type="text" value={feature} onChange={(e) => updateFeature(index, e.target.value)} placeholder="Feature" />
                    <button type="button" className="pp-btn" onClick={() => removeFeature(index)} aria-label="Remove feature"><X size={14} /></button>
                  </div>
                ))}
                <button type="button" className="pp-btn" onClick={addFeature} style={{ marginTop: 4 }}>+ Add feature</button>
              </div>
              <div className="pp-checks" style={{ marginTop: 12 }}>
                <label><input type="checkbox" checked={formData.isActive} onChange={(e) => setFormData((prev) => ({ ...prev, isActive: e.target.checked }))} /> Active</label>
                <label><input type="checkbox" checked={formData.popular} onChange={(e) => setFormData((prev) => ({ ...prev, popular: e.target.checked }))} /> Popular</label>
                <label><input type="checkbox" checked={formData.isFree} onChange={(e) => setFormData((prev) => ({ ...prev, isFree: e.target.checked }))} /> Free</label>
                <label><input type="checkbox" checked={formData.isCustom} onChange={(e) => setFormData((prev) => ({ ...prev, isCustom: e.target.checked }))} /> Custom pricing</label>
              </div>
            </div>
            <div className="pp-modal-footer">
              <button type="button" className="pp-btn" onClick={() => setShowModal(false)}>Cancel</button>
              <button type="button" className="pp-btn pp-btn--primary" onClick={handleSave}>
                <Save size={14} /> Save plan
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeleteConfirm && (
        <div className="pp-overlay" onClick={() => setShowDeleteConfirm(null)}>
          <div className="pp-modal pp-modal--sm" onClick={(e) => e.stopPropagation()}>
            <div className="pp-modal-body">
              <div className="pp-confirm-icon"><AlertTriangle size={22} /></div>
              <p className="pp-confirm-text">Delete this pricing plan? This cannot be undone.</p>
            </div>
            <div className="pp-modal-footer">
              <button type="button" className="pp-btn" onClick={() => setShowDeleteConfirm(null)}>Cancel</button>
              <button type="button" className="pp-btn pp-btn--danger" onClick={() => handleDelete(showDeleteConfirm)}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PricingPlansManager;

